// Enhanced Wishlist JavaScript with Section Support and Add to Cart
// Update your assets/wishlist.js with this code

class Wishlist {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.wishlistItems = this.getWishlistFromStorage();
    this.init();
  }

  init() {
    this.updateWishlistUI();
    this.bindEvents();
    this.initializeSections();
  }

  bindEvents() {
    // Bind wishlist toggle buttons
    document.addEventListener('click', (e) => {
      if (e.target.matches('.wishlist-btn, .wishlist-btn *')) {
        e.preventDefault();
        const btn = e.target.closest('.wishlist-btn');
        const productId = btn.dataset.productId;
        const productHandle = btn.dataset.productHandle;
        const productTitle = btn.dataset.productTitle;
        const productImage = btn.dataset.productImage;
        const productPrice = btn.dataset.productPrice;
        const productCompareAtPrice = btn.dataset.productCompareAtPrice;
        const variantId = btn.dataset.variantId;
        
        this.toggleWishlist({
          id: productId,
          handle: productHandle,
          title: productTitle,
          image: productImage,
          price: productPrice,
          compare_at_price: productCompareAtPrice,
          variant_id: variantId || this.getFirstVariantId(productHandle)
        });
      }
    });

    // Remove from wishlist
    document.addEventListener('click', (e) => {
      if (e.target.matches('.wishlist-remove, .wishlist-remove *')) {
        e.preventDefault();
        const btn = e.target.closest('.wishlist-remove');
        const productId = btn.dataset.productId;
        this.removeFromWishlist(productId);
      }
    });

    // Add to cart from wishlist
    document.addEventListener('click', (e) => {
      if (e.target.matches('.wishlist-add-to-cart, .wishlist-add-to-cart *')) {
        e.preventDefault();
        const btn = e.target.closest('.wishlist-add-to-cart');
        const variantId = btn.dataset.variantId;
        const productTitle = btn.dataset.productTitle;
        this.addToCart(variantId, productTitle, btn);
      }
    });

    // Clear wishlist button
    document.addEventListener('click', (e) => {
      if (e.target.matches('.wishlist-clear-btn')) {
        e.preventDefault();
        this.clearWishlist();
      }
    });
  }

  initializeSections() {
    // Initialize all wishlist sections on the page
    const wishlistSections = document.querySelectorAll('.wishlist-section');
    wishlistSections.forEach(section => {
      this.updateWishlistSection(section);
    });
  }

  getWishlistFromStorage() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch (e) {
      return [];
    }
  }

  saveWishlistToStorage() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.wishlistItems));
  }

  // Get first available variant ID for a product
  async getFirstVariantId(productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (response.ok) {
        const product = await response.json();
        return product.variants && product.variants.length > 0 ? product.variants[0].id : null;
      }
    } catch (error) {
      console.error('Error fetching product variants:', error);
    }
    return null;
  }

  async addToWishlist(product) {
    if (!this.isInWishlist(product.id)) {
      // Ensure we have a variant_id
      if (!product.variant_id && product.handle) {
        product.variant_id = await this.getFirstVariantId(product.handle);
      }
      
      this.wishlistItems.push(product);
      this.saveWishlistToStorage();
      this.updateWishlistUI();
      this.showNotification(`${product.title} added to wishlist`, 'success');
    }
  }

  removeFromWishlist(productId) {
    const index = this.wishlistItems.findIndex(item => item.id == productId);
    if (index !== -1) {
      const product = this.wishlistItems[index];
      this.wishlistItems.splice(index, 1);
      this.saveWishlistToStorage();
      this.updateWishlistUI();
      this.showNotification(`${product.title} removed from wishlist`, 'info');
    }
  }

  toggleWishlist(product) {
    if (this.isInWishlist(product.id)) {
      this.removeFromWishlist(product.id);
    } else {
      this.addToWishlist(product);
    }
  }

  isInWishlist(productId) {
    return this.wishlistItems.some(item => item.id == productId);
  }

  clearWishlist() {
    if (this.wishlistItems.length === 0) return;
    
    if (confirm('Are you sure you want to clear your entire wishlist?')) {
      this.wishlistItems = [];
      this.saveWishlistToStorage();
      this.updateWishlistUI();
      this.showNotification('Wishlist cleared', 'info');
    }
  }

  // Add to Cart functionality
  async addToCart(variantId, productTitle, button) {
    // If no variant ID, try to fetch it
    if (!variantId || variantId === 'null' || variantId === null) {
      const productHandle = button.dataset.productHandle;
      if (productHandle) {
        variantId = await this.getFirstVariantId(productHandle);
        if (variantId) {
          // Update the stored product with the variant ID
          const productIndex = this.wishlistItems.findIndex(item => item.handle === productHandle);
          if (productIndex !== -1) {
            this.wishlistItems[productIndex].variant_id = variantId;
            this.saveWishlistToStorage();
          }
        }
      }
    }

    if (!variantId) {
      this.showNotification('Product variant not found', 'error');
      return;
    }

    // Show loading state
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (response.ok) {
        // Update cart count if it exists
        this.updateCartCount();
        this.showNotification(`${productTitle} added to cart!`, 'success');
      } else {
        const error = await response.json();
        this.showNotification(error.description || 'Could not add to cart', 'error');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showNotification('Something went wrong. Please try again.', 'error');
    } finally {
      // Reset button state
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  // Update cart count in header/drawer if it exists
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        const cartCountElements = document.querySelectorAll('.cart-count, #cart-count, [data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });
        
        // Trigger cart drawer update if it exists
        if (window.theme && window.theme.cartDrawer) {
          window.theme.cartDrawer.refresh();
        }
      }
    } catch (error) {
      console.error('Cart count update error:', error);
    }
  }

  updateWishlistUI() {
    // Update wishlist count
    const countElements = document.querySelectorAll('#wishlist-count-number, .wishlist-count');
    const countBubble = document.getElementById('wishlist-count-bubble');
    const countTextElements = document.querySelectorAll('.wishlist-count-text');
    const count = this.wishlistItems.length;

    countElements.forEach(el => el.textContent = count);
    countTextElements.forEach(el => {
      el.textContent = count === 1 ? 'item in wishlist' : 'items in wishlist';
    });
    
    if (countBubble) {
      if (count > 0) {
        countBubble.style.display = 'block';
      } else {
        countBubble.style.display = 'none';
      }
    }

    // Update wishlist buttons
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const productId = btn.dataset.productId;
      const isInWishlist = this.isInWishlist(productId);
      
      btn.classList.toggle('wishlist-btn--active', isInWishlist);
      btn.setAttribute('aria-label', isInWishlist ? 'Remove from wishlist' : 'Add to wishlist');
      
      const icon = btn.querySelector('.wishlist-icon');
      if (icon) {
        icon.innerHTML = isInWishlist ? this.getFilledHeartSVG() : this.getEmptyHeartSVG();
      }

      // Update button text if visible
      const addText = btn.querySelector('.wishlist-text--add');
      const removeText = btn.querySelector('.wishlist-text--remove');
      if (addText && removeText) {
        if (isInWishlist) {
          addText.style.display = 'none';
          removeText.style.display = 'inline';
        } else {
          addText.style.display = 'inline';
          removeText.style.display = 'none';
        }
      }
    });

    // Update wishlist page if it exists
    this.updateWishlistPage();

    // Update all wishlist sections
    const wishlistSections = document.querySelectorAll('.wishlist-section');
    wishlistSections.forEach(section => {
      this.updateWishlistSection(section);
    });
  }

  updateWishlistPage() {
    const wishlistContainer = document.querySelector('.wishlist-products');
    if (!wishlistContainer) return;

    if (this.wishlistItems.length === 0) {
      wishlistContainer.innerHTML = `
        <div class="wishlist-empty">
          <h3>Your wishlist is empty</h3>
          <p>Add products you love to your wishlist</p>
          <a href="/collections/all" class="btn btn--primary">Continue Shopping</a>
        </div>
      `;
    } else {
      const productsHTML = this.wishlistItems.map(product => this.getProductCardHTML(product)).join('');
      wishlistContainer.innerHTML = `<div class="wishlist-grid">${productsHTML}</div>`;
    }
  }

  updateWishlistSection(section) {
    const loading = section.querySelector('.wishlist-loading');
    const empty = section.querySelector('.wishlist-empty');
    const grid = section.querySelector('.wishlist-products-grid');

    if (!loading || !empty || !grid) return;

    // Hide loading
    loading.style.display = 'none';

    if (this.wishlistItems.length === 0) {
      empty.style.display = 'block';
      grid.style.display = 'none';
    } else {
      empty.style.display = 'none';
      grid.style.display = 'flex';
      
      const productsHTML = this.wishlistItems.map(product => 
        this.getProductCardHTML(product, true)
      ).join('');
      grid.innerHTML = productsHTML;
    }
  }

  getProductCardHTML(product, isSection = false) {
    const cardClass = isSection ? 'wishlist-product-card' : 'wishlist-product-card';

    const price = product.price;
    const compareAtPrice = product.compare_at_price;
    const hasDiscount = compareAtPrice ? compareAtPrice : false;

    let priceHTML = '';
    if (hasDiscount) {
      priceHTML = `
        <span class="price">
          <s class="price-item regular-price">${compareAtPrice}</s>
          <span class="price-item purchase-price">${price}</span>
        </span>
      `;
    } else {
      priceHTML = `
        <span class="price">
          <span class="price-item purchase-price">${price}</span>
        </span>
      `;
    }

    // Ensure we have variant_id for add to cart functionality
    const hasVariantId = product.variant_id && product.variant_id !== 'null' && product.variant_id !== null;

    return `
      <div class="${cardClass}" data-product-id="${product.id}">
        <div class="wishlist-product-image">
          <a href="/products/${product.handle}">
            <img src="${product.image}" alt="${product.title}" loading="lazy">
          </a>
        </div>
        <div class="wishlist-product-info">
          <h3><a href="/products/${product.handle}">${product.title}</a></h3>
          <p class="wishlist-product-price">${priceHTML}</p>
          <div class="wishlist-product-actions">
            <button class="button button--primary wishlist-add-to-cart" 
                    data-variant-id="${product.variant_id || ''}" 
                    data-product-title="${product.title}"
                    data-product-handle="${product.handle}">
              Add to Cart
            </button>
            <button class="button button--secondary wishlist-remove" data-product-id="${product.id}">
              Remove
            </button>
          </div>
        </div>
      </div>
    `;
  }

  showNotification(message, type = 'success') {
    // Create or update notification
    let notification = document.querySelector('.wishlist-notification');
    if (!notification) {
      notification = document.createElement('div');
      notification.className = 'wishlist-notification';
      document.body.appendChild(notification);
    }

    notification.textContent = message;
    notification.className = `wishlist-notification wishlist-notification--${type} wishlist-notification--show`;

    setTimeout(() => {
      notification.classList.remove('wishlist-notification--show');
    }, 3000);
  }

  getEmptyHeartSVG() {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>`;
  }

  getFilledHeartSVG() {
    return `<svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>`;
  }

  // Public methods for external use
  getWishlistItems() {
    return this.wishlistItems;
  }

  getWishlistCount() {
    return this.wishlistItems.length;
  }
}

// Initialize wishlist when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.shopifyWishlist = new Wishlist();
});